import { useEffect, useState } from "react";
import { Share, SquarePlus, Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePWAInstall } from "@/hooks/use-pwa-install";

const DISMISS_KEY = "aurora-install-prompt-dismissed";
const SHOW_DELAY_MS = 20_000;

/**
 * One-time "add this app to your phone" hint. Shown after a short delay so it
 * never competes with the first-visit tutorial, and never shown again once
 * dismissed or when already running as an installed app.
 */
export function InstallAppPrompt() {
    const { canInstall, promptInstall, isStandalone, isIOS } = usePWAInstall();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (isStandalone) return;
        if (localStorage.getItem(DISMISS_KEY) === "true") return;
        // Only worth prompting when we can actually install (Android) or give
        // manual instructions (iOS Safari).
        if (!canInstall && !isIOS) return;

        const id = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
        return () => clearTimeout(id);
    }, [canInstall, isIOS, isStandalone]);

    if (!visible || isStandalone) return null;

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, "true");
        setVisible(false);
    };

    return (
        <div
            role="dialog"
            aria-label="Install Aurora Forecaster"
            className="fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-[1090] mx-auto max-w-md rounded-xl border border-border bg-popover/95 p-4 text-popover-foreground shadow-lg backdrop-blur supports-[backdrop-filter]:bg-popover/85"
        >
            <div className="flex items-start gap-3">
                <Download className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">Install Aurora Forecaster</p>
                    {isIOS && !canInstall ? (
                        <p className="text-sm text-muted-foreground">
                            Tap <Share className="inline size-3.5 align-[-2px]" aria-label="Share" /> Share,
                            then{" "}
                            <span className="whitespace-nowrap">
                                <SquarePlus
                                    className="inline size-3.5 align-[-2px]"
                                    aria-hidden="true"
                                />{" "}
                                Add to Home Screen
                            </span>{" "}
                            to use it like an app — including offline in the field.
                        </p>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Get the app on your home screen — faster to open and works offline in the
                            field.
                        </p>
                    )}
                </div>
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={dismiss}
                    aria-label="Dismiss install prompt"
                    className="size-7 shrink-0"
                >
                    <X className="size-4" aria-hidden="true" />
                </Button>
            </div>
            {canInstall && (
                <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={dismiss}>
                        Not now
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => {
                            void promptInstall().then((accepted) => {
                                if (accepted) setVisible(false);
                            });
                        }}
                    >
                        Install
                    </Button>
                </div>
            )}
        </div>
    );
}
